const EM_DASH = "—";

// Short display name for a Windmill flow path like "f/jira_bug_analysis_fix_compare/main".
// Drops the leading "f/" and trailing "/main", leaving the middle segment.
export function flowShortName(flowPath: string | null | undefined): string {
  if (!flowPath) return EM_DASH;
  return flowPath.replace(/^f\//, "").replace(/\/main$/, "") || flowPath;
}

// Friendly label for the flows people launch from mission control.
const FLOW_LABELS: Record<string, string> = {
  "f/jira_bug_analysis_deep/main": "Analyze",
  "f/jira_bug_analysis_fix/main": "Fix",
  "f/jira_bug_analysis_fix_fullstack/main": "Fullstack fix",
  "f/jira_bug_analysis_fix_codex/main": "Fix · Codex",
  "f/jira_bug_analysis_fix_compare/main": "Compare",
  "f/jira_bug_analysis_iterate/main": "Iterate",
  "f/jira_sprint_scorecard/main": "Scorecard",
  "f/jira_sprint_scorecard_active/main": "Scorecard (active)",
  "f/jira_sprint_scorecard_onboard/main": "Scorecard onboard",
};

export function flowLabel(flowPath: string | null | undefined): string {
  if (!flowPath) return EM_DASH;
  return FLOW_LABELS[flowPath] ?? flowShortName(flowPath).replace(/_/g, " ");
}

export function money(usd: number | null | undefined, digits = 2): string {
  if (usd === null || usd === undefined || Number.isNaN(usd)) return EM_DASH;
  return `$${usd.toFixed(digits)}`;
}

export function duration(ms: number | null | undefined): string {
  if (ms === null || ms === undefined || ms < 0) return EM_DASH;
  const s = Math.round(ms / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ${String(s % 60).padStart(2, "0")}s`;
  return `${Math.floor(m / 60)}h ${String(m % 60).padStart(2, "0")}m`;
}

export function ago(iso: string | number | null | undefined, now = Date.now()): string {
  if (iso === null || iso === undefined || iso === "") return EM_DASH;
  const t = typeof iso === "number" ? iso : Date.parse(iso);
  if (Number.isNaN(t)) return EM_DASH;
  const s = Math.max(0, Math.round((now - t) / 1000));
  if (s < 45) return "just now";
  const m = Math.round(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.round(h / 24);
  return d === 1 ? "yesterday" : `${d}d ago`;
}

export function clock(iso: string | number | null | undefined): string {
  if (iso === null || iso === undefined || iso === "") return EM_DASH;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return EM_DASH;
  return d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
}

export function compact(n: number | null | undefined): string {
  if (n === null || n === undefined) return EM_DASH;
  if (n < 1000) return String(n);
  if (n < 1_000_000) return `${(n / 1000).toFixed(n < 10_000 ? 1 : 0)}k`;
  return `${(n / 1_000_000).toFixed(1)}M`;
}

// "Data-and-AI/foodics-labeeb" → "foodics-labeeb"
export function repoName(repo: string | null | undefined): string {
  if (!repo) return EM_DASH;
  return repo.split("/").pop() || repo;
}

export function prNumber(url: string | null | undefined): string | null {
  const m = url?.match(/\/pull\/(\d+)/);
  return m ? `#${m[1]}` : null;
}
